import { useCallback, useEffect, useState } from "react";
import { queryActivityTimeline } from "@/lib/activityTimelineClient";
import {
  dateInputToSinceIso,
  dateInputToUntilIso,
  defaultForensicsDateInputs,
} from "@/lib/activityTimeline/forensicsDateRange";
import type {
  ActivityTimelineScope,
  TimelineDomain,
  TimelineEntry,
  TimelineSource,
  TimelineTestDataFilter,
} from "@/lib/activityTimeline/types";
import { TIMELINE_DEFAULT_LIMIT } from "@/lib/activityTimeline/types";
import { FORENSICS_DEFAULT_PAGE_SIZE, forensicsPageCount } from "@/lib/activityTimeline/forensicsPaging";

export type ActivityTimelineFilterState = {
  sinceDate: string;
  untilDate: string;
  actorUserId: string;
  actorLabelContains: string;
  domain: "" | TimelineDomain;
  entityType: string;
  entityId: string;
  action: string;
  q: string;
  source: "" | TimelineSource;
  testData: TimelineTestDataFilter;
};

export function initialActivityTimelineFilters(
  opts: { includeSource?: boolean } = {}
): ActivityTimelineFilterState {
  const dates = defaultForensicsDateInputs();
  return {
    ...dates,
    actorUserId: "",
    actorLabelContains: "",
    domain: "",
    entityType: "",
    entityId: "",
    action: "",
    q: "",
    source: opts.includeSource ? "" : "",
    testData: "exclude",
  };
}

function buildTimelineQuery(
  scope: ActivityTimelineScope,
  organizationId: string,
  filters: ActivityTimelineFilterState,
  offset: number,
  limit: number
) {
  return {
    scope,
    ...(scope === "platform" ? { organization_id: organizationId } : {}),
    since: dateInputToSinceIso(filters.sinceDate),
    until: dateInputToUntilIso(filters.untilDate),
    actor_user_id: filters.actorUserId.trim() || undefined,
    actor_label_contains: filters.actorLabelContains.trim() || undefined,
    domain: filters.domain || undefined,
    entity_type: filters.entityType.trim() || undefined,
    entity_id: filters.entityId.trim() || undefined,
    action: filters.action.trim() || undefined,
    q: filters.q.trim() || undefined,
    source: filters.source || undefined,
    test_data: filters.testData,
    limit,
    offset,
  };
}

export function useActivityTimeline(opts: {
  enabled: boolean;
  scope: ActivityTimelineScope;
  organizationId: string;
  includeSourceFilter?: boolean;
  paged?: boolean;
  defaultPageSize?: number;
}) {
  const { enabled, scope, organizationId, includeSourceFilter = scope === "platform" } = opts;
  const paged = Boolean(opts.paged);
  const [pageSize, setPageSizeState] = useState(opts.defaultPageSize ?? FORENSICS_DEFAULT_PAGE_SIZE);
  const [page, setPageState] = useState(0);
  const [filters, setFilters] = useState<ActivityTimelineFilterState>(() =>
    initialActivityTimelineFilters({ includeSource: includeSourceFilter })
  );
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const queryLimit = paged ? pageSize : TIMELINE_DEFAULT_LIMIT;
  const [sourcesQueried, setSourcesQueried] = useState<TimelineSource[]>([]);
  const [sourcesUnavailable, setSourcesUnavailable] = useState<TimelineSource[]>([]);
  const [federationTruncated, setFederationTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const forbiddenMessage =
    scope === "platform"
      ? "Platform forensics role required."
      : "Activity timeline is restricted to org admins / owners.";

  const applyResult = useCallback(
    (r: Awaited<ReturnType<typeof queryActivityTimeline>>, mode: "replace" | "append", nextOffset: number) => {
      if (!r.ok) {
        setError(r.status === 403 ? forbiddenMessage : r.error);
        if (mode === "replace") {
          setEntries([]);
          setTotal(0);
          setHasMore(false);
        }
        return;
      }
      const page = r.data.entries ?? [];
      setEntries((prev) => (mode === "append" ? [...prev, ...page] : page));
      setTotal(r.data.total ?? 0);
      setHasMore(Boolean(r.data.hasMore));
      setSourcesQueried(r.data.sources_queried ?? []);
      setSourcesUnavailable(r.data.sources_unavailable ?? []);
      setFederationTruncated(Boolean(r.data.federation_truncated));
      setOffset(nextOffset);
      setError(null);
    },
    [forbiddenMessage]
  );

  const fetchPage = useCallback(
    async (nextOffset: number, mode: "replace" | "append") => {
      if (!enabled || !organizationId) return;
      setWorking(true);
      const r = await queryActivityTimeline(
        buildTimelineQuery(scope, organizationId, filters, nextOffset, queryLimit)
      );
      setWorking(false);
      applyResult(r, mode, nextOffset);
    },
    [enabled, organizationId, scope, filters, applyResult, queryLimit]
  );

  const refresh = useCallback(async () => {
    await fetchPage(paged ? page * pageSize : 0, "replace");
  }, [fetchPage, paged, page, pageSize]);

  const loadMore = useCallback(async () => {
    if (paged || !hasMore || working) return;
    const next = offset + queryLimit;
    await fetchPage(next, "append");
  }, [paged, hasMore, working, offset, fetchPage, queryLimit]);

  const setPage = useCallback((next: number) => {
    setPageState(Math.max(0, next));
  }, []);

  const setPageSize = useCallback((next: number) => {
    setPageSizeState(next);
    setPageState(0);
  }, []);

  useEffect(() => {
    setPageState(0);
  }, [organizationId]);

  useEffect(() => {
    if (!enabled || !organizationId) {
      setEntries([]);
      setTotal(0);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const nextOffset = paged ? page * pageSize : 0;
    void (async () => {
      const r = await queryActivityTimeline(
        buildTimelineQuery(scope, organizationId, filters, nextOffset, queryLimit)
      );
      if (cancelled) return;
      setLoading(false);
      applyResult(r, "replace", nextOffset);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    organizationId,
    scope,
    filters.sinceDate,
    filters.untilDate,
    filters.actorUserId,
    filters.actorLabelContains,
    filters.domain,
    filters.entityType,
    filters.entityId,
    filters.action,
    filters.q,
    filters.source,
    filters.testData,
    applyResult,
    paged,
    page,
    pageSize,
    queryLimit,
  ]);

  const patchFilters = useCallback((patch: Partial<ActivityTimelineFilterState>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setOffset(0);
    setPageState(0);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialActivityTimelineFilters({ includeSource: includeSourceFilter }));
    setOffset(0);
    setPageState(0);
  }, [includeSourceFilter]);

  const enterEntityDrill = useCallback((entityId: string, entityType?: string) => {
    setFilters((f) => ({
      ...f,
      entityId,
      entityType: entityType?.trim() || f.entityType,
    }));
    setOffset(0);
    setPageState(0);
  }, []);

  const clearEntityDrill = useCallback(() => {
    setFilters((f) => ({ ...f, entityId: "" }));
    setOffset(0);
    setPageState(0);
  }, []);

  return {
    filters,
    patchFilters,
    resetFilters,
    enterEntityDrill,
    clearEntityDrill,
    entries,
    total,
    hasMore,
    sourcesQueried,
    sourcesUnavailable,
    federationTruncated,
    loading,
    working,
    error,
    refresh,
    loadMore,
    page,
    pageSize,
    pageCount: forensicsPageCount(total, pageSize),
    setPage,
    setPageSize,
  };
}
